import React, { useState } from "react";
import usePack from "../../../../hooks/shared/packs/usePack";
import type { PackType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { BnbLabel, useBnbOptionalLabel } from "../../../../utils/labels";
import Popover from "../../../../../../../common-deprecated/components/popover/aem/lexus/Popover";
import * as Button from "../../../../../../../common-deprecated/styles/v2/lexus/components/Button";
import * as Text from "../../../../../../../common-deprecated/styles/v2/lexus/utilities/Text";
import ItemList from "../../common/ItemList";
import AccentColours from "../../common/extras/AccentColours";
import Extra from "../../common/extras/Extra";
import OptionalPackEquipment from "./OptionalPackEquipment";
import * as Styles from "./styles/Pack";
import {
    ToggleIconWrapper,
    IconWrapper,
} from "../../../../../../../common-deprecated/styles/v2/lexus/globals/IconWrapper";
import Icon from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import * as Type from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Type";
import Price from "../../common/price/Price";
import ImageFluid from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Image";
import { NavStep } from "../../../../../shared-logic/utils/Navigation";
import { trackPackViewFeatures, trackCustomizeConfigItem } from "../../../../utils/tracking";
import { Ellipsis } from "../../../../../../../common-deprecated/styles/v2/lexus/utilities/Ellipsis";

type PropsType = {
    pack: PackType;
    showImage: boolean;
    packIndex: number;
    isSinglePack?: boolean; // This will hide the actions and show an opened state of a pack
};

const Pack = (props: PropsType): JSX.Element | null => {
    const { pack, showImage, packIndex, isSinglePack } = props;
    const [isOpen, setOpen] = useState<boolean>(!!isSinglePack);
    const packProps = usePack(pack);

    const [includedPack, disabledPack] = useBnbOptionalLabel(["includedPack", "disabledPack"]);

    if (!packProps) return null;

    const { actions, packContent } = packProps;
    const { selectPack, deselectPack, selectPrimaryAccentColour } = actions;
    const {
        selected,
        enabled,
        price,
        primaryAccentColour,
        primaryAccentColours,
        colours,
        upholsteries,
        wheels,
        renderedEquipment,
        totalPackContent,
        optionalEquipment,
    } = packContent;

    const { id, name, image, footNote, description } = pack;
    const hasImage = showImage && image;

    return (
        <Styles.Wrapper packIndex={packIndex} selected={selected} isSinglePack={isSinglePack} disableBorder={!enabled}>
            <Styles.Header hasBackground={showImage && !!image} hasImage={!!hasImage}>
                {hasImage && <ImageFluid src={image!} alt={pack.name} />}
                <Styles.TitleWrapper hasImage={!!hasImage}>
                    <Type.Strong mb={1} color="midnight" display="block">
                        <Ellipsis maxWidth={{ _: "280px", md: "100%" }}>{name}</Ellipsis>
                        {description && <Popover showOnClick content={description} useHTML />}
                    </Type.Strong>
                    <Styles.PriceWrapper>
                        <Price price={price} financeItemId={id} />
                    </Styles.PriceWrapper>
                    {primaryAccentColours.length > 1 && (
                        <AccentColours
                            selectedColour={primaryAccentColour}
                            colours={primaryAccentColours}
                            selectColour={selectPrimaryAccentColour}
                            enabled={enabled && selected}
                        />
                    )}
                </Styles.TitleWrapper>
            </Styles.Header>

            {!isSinglePack && !!footNote && <Styles.Footnote>{footNote}</Styles.Footnote>}

            {!isSinglePack && (
                <Styles.PackAction isActive={isOpen}>
                    <Button.ViewMore
                        m={0}
                        onClick={() => {
                            if (!isOpen) trackPackViewFeatures();
                            setOpen(!isOpen);
                        }}
                    >
                        <BnbLabel label="packsShowFeatures" params={{ count: totalPackContent }} />
                        <ToggleIconWrapper ml={1} color="azureBlue" isDown={isOpen}>
                            <Icon variant="chevronDown" />
                        </ToggleIconWrapper>
                    </Button.ViewMore>
                    <Button.Checkbox
                        type="button"
                        selected={selected}
                        disabled={!enabled}
                        onClick={() => {
                            if (selected) {
                                deselectPack();
                                trackCustomizeConfigItem(NavStep.Customize, "remove-extra");
                            } else {
                                selectPack();
                                setOpen(true);
                                trackCustomizeConfigItem(NavStep.Customize, "add-extra");
                            }
                        }}
                    >
                        {selected && !enabled && includedPack && <Text.Button>{includedPack}</Text.Button>}
                        {!selected && !enabled && disabledPack && <Text.Button>{disabledPack}</Text.Button>}
                        <IconWrapper>
                            <Icon variant={selected ? "close" : "plus"} />
                        </IconWrapper>
                    </Button.Checkbox>
                </Styles.PackAction>
            )}
            {isOpen && (
                <>
                    <Styles.ContentWrapper isOpen={isOpen}>
                        {colours.length > 0 && <ItemList activeItemType="colour" items={colours} hasBorder />}
                        {wheels.length > 1 && <ItemList activeItemType="wheel" items={wheels} hasBorder />}
                        {upholsteries.length > 1 && (
                            <ItemList activeItemType="upholstery" items={upholsteries} hasBorder />
                        )}

                        {renderedEquipment.length > 0 && (
                            <Styles.Grid>
                                {renderedEquipment.map((extra) => (
                                    <Extra key={extra.id} extra={extra} enabled />
                                ))}
                            </Styles.Grid>
                        )}
                    </Styles.ContentWrapper>

                    {optionalEquipment.length > 0 && (
                        <OptionalPackEquipment
                            equipment={packContent.optionalEquipment}
                            selectedEquipment={packContent.selectedEquipment}
                            equipmentSelect={actions.equipmentSelect}
                            equipmentDeselect={actions.equipmentDeselect}
                            secondaryAccentColour={packContent.secondaryAccentColour}
                            secondaryAccentColours={packContent.secondaryAccentColours}
                            selectSecondaryAccentColour={actions.selectSecondaryAccentColour}
                            enabled={enabled && selected}
                        />
                    )}
                </>
            )}
        </Styles.Wrapper>
    );
};

export default Pack;
